import {
  PostExpandedListItemSkeleton,
  PostExpandedViewCard,
  PostGridItemSkeleton,
  PostListItemSkeleton,
} from '@nucleus/post-theme-elements';
import { MediaExpandedList, MediaGrid } from '@nucleus/sermon-theme-elements';
import React from 'react';
import styled from 'styled-components';

type PostBlockSkeletonLayout = 'expanded' | 'list' | 'filmstrip' | 'grid';

interface PostBlockSkeletonProps {
  layout?: PostBlockSkeletonLayout;
  count?: number;
}

export const PostBlockSkeleton = ({ layout, count = 6 }: PostBlockSkeletonProps): JSX.Element => {
  const items = Array.from({ length: count }, (_, index) => index);

  switch (layout) {
    case 'expanded':
      return (
        <ExpandedViewContainer>
          <PostExpandedViewCard>
            <MediaExpandedList>
              {items.slice(0, 3).map((index) => (
                <PostExpandedListItemSkeleton key={index} />
              ))}
            </MediaExpandedList>
          </PostExpandedViewCard>
        </ExpandedViewContainer>
      );
    case 'list':
      return (
        <ListSkeletonContainer>
          {items.map((index) => (
            <PostListItemSkeleton key={index} />
          ))}
        </ListSkeletonContainer>
      );
    case 'filmstrip':
      return (
        <FilmstripSkeletonContainer>
          {items.map((index) => (
            <PostGridItemSkeleton key={index} />
          ))}
        </FilmstripSkeletonContainer>
      );
    case 'grid':
    default:
      return (
        <GridSkeletonContainer>
          {items.map((index) => (
            <PostGridItemSkeleton key={index} />
          ))}
        </GridSkeletonContainer>
      );
  }
};

export const CollectionBlockSkeleton = (): JSX.Element => {
  return (
    <GridSkeletonContainer>
      {Array.from({ length: 4 }, (_, index) => (
        <PostGridItemSkeleton key={index} />
      ))}
    </GridSkeletonContainer>
  );
};

const GridSkeletonContainer = styled(MediaGrid)`
  --min-item-size: 300px;
  --row-gap: 18px;
  margin-bottom: 36px;
`;

const ListSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExpandedViewContainer = styled.div`
  @media (min-width: 600px) {
    padding: calc(var(--unit-length) * 1);
  }
`;

const FilmstripSkeletonContainer = styled.div`
  display: flex;
  gap: 12px;
  overflow: hidden;
  padding-block: 36px;
  margin-block: -36px;

  > * {
    flex: 0 0 min(100%, max(calc(33% - 6px), 300px));
  }
`;
